/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useEffect, useState } from "react";
import { Route, Redirect } from "react-router-dom";
import { useUser } from "contexts/UserContext";
import SplashScreen from "components/SplashScreen/SplashScreen";

/**
 * RoleProtectedRoute - Role bazlı route koruması
 * Component mount olmadan önce role kontrolü yapar
 * @param {React.Component} component - Render edilecek component
 * @param {string[]} allowedRoles - Bu route'a erişebilecek roller
 * @param {boolean} adminOnly - Sadece admin için (allowedRoles yerine kullanılabilir)
 * @param {object} rest - Route'un diğer props'ları
 */
const RoleProtectedRoute = ({ 
  component: Component, 
  allowedRoles = [],
  adminOnly = false,
  ...rest 
}) => {
  const { user, loading: userLoading, userRole, userBayiTipi } = useUser();
  const [hasAccess, setHasAccess] = useState(null); // null = henüz kontrol edilmedi

  useEffect(() => {
    // Loading durumunda veya user yoksa erişim kontrolü yapma
    if (userLoading) {
      return;
    }

    // User yoksa erişim yok
    if (!user) {
      setHasAccess(false);
      return;
    }

    const role = userRole;
    const bayi_tipi = userBayiTipi;
    
    // Admin only kontrolü
    if (adminOnly) {
      setHasAccess(role === "admin");
      return;
    }
    
    // Allowed roles kontrolü
    if (allowedRoles.length > 0) {
      let access = allowedRoles.includes(role);
      
      // Bayi tipi kontrolü: Stok ve Sim yönetimi sadece saas_bayi görebilir
      if (role === "bayi" && rest.path && (rest.path.includes("/stok-yonetimi") || rest.path.includes("/sim-yonetimi"))) {
        access = access && bayi_tipi === "saas_bayi";
      }
      
      // Teknik personel SIM yönetimine erişemez
      if (role === "teknik_eleman" && rest.path && rest.path.includes("/sim-yonetimi")) {
        access = false;
      }
      
      setHasAccess(access);
    } else {
      // Eğer allowedRoles belirtilmemişse, herkese izin ver (sadece authenticated)
      setHasAccess(true);
    }
  }, [userLoading, user, userRole, userBayiTipi, allowedRoles, adminOnly, rest.path]);

  // Loading durumunda splash screen göster
  if (userLoading || hasAccess === null) {
    return <SplashScreen isLoading={true} message="Yükleniyor..." fullScreen={true} />;
  }

  // User yoksa login'e yönlendir
  if (!user) {
    return <Redirect to="/login" />;
  }

  // Erişim yoksa role'e göre uygun sayfaya yönlendir
  if (!hasAccess) {
    let redirectTo = "/admin/dashboard";
    
    if (userRole === "teknik_eleman") {
      redirectTo = "/admin/araclar";
    } else if (userRole === "bayi") {
      redirectTo = "/admin/dashboard";
    }
    
    return <Redirect to={redirectTo} />;
  }

  return (
    <Route
      {...rest}
      render={(props) => <Component {...props} />}
    />
  );
};

export default RoleProtectedRoute;

